import { useRef, useEffect, useMemo, useCallback } from "react";
import { ArrowLeft, Search, Check, ChevronDown } from "lucide-react";
import { useFilter } from "./FilterContext";
import type { FilterCategory, FilterOperator } from "./types";
import { OPERATOR_LABELS } from "./types";

export function FilterValuePicker() {
  const {
    step,
    categoryGroups,
    filters,
    searchQuery,
    setSearchQuery,
    setStep,
    addFilter,
    updateFilter,
    removeFilter,
  } = useFilter();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Determine which category and existing filter we're working with
  const categoryId =
    step.type === "value_picker"
      ? step.categoryId
      : step.type === "editing_pill"
        ? filters.find((f) => f.id === step.filterId)?.categoryId
        : undefined;

  const category = useMemo<FilterCategory | undefined>(() => {
    for (const group of categoryGroups) {
      const found = group.categories.find((c) => c.id === categoryId);
      if (found) return found;
    }
    return undefined;
  }, [categoryGroups, categoryId]);

  const existingFilter = useMemo(() => {
    if (step.type === "editing_pill") {
      return filters.find((f) => f.id === step.filterId);
    }
    return filters.find((f) => f.categoryId === categoryId);
  }, [filters, categoryId, step]);

  const selectedOperator: FilterOperator =
    existingFilter?.operator ?? category?.allowedOperators[0] ?? "is";

  const selectedValueIds: string[] = existingFilter?.valueIds ?? [];

  const filteredValues = useMemo(
    () =>
      category?.values.filter((v) =>
        v.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ) ?? [],
    [category, searchQuery],
  );

  const toggleValue = useCallback(
    (valueId: string) => {
      if (!category) return;

      const isSelected = selectedValueIds.includes(valueId);
      const newValueIds = isSelected
        ? selectedValueIds.filter((id) => id !== valueId)
        : [...selectedValueIds, valueId];

      if (existingFilter) {
        if (newValueIds.length === 0) {
          removeFilter(existingFilter.id);
        } else {
          updateFilter(existingFilter.id, { valueIds: newValueIds });
        }
      } else if (newValueIds.length > 0) {
        addFilter({
          id: crypto.randomUUID(),
          categoryId: category.id,
          operator: selectedOperator,
          valueIds: newValueIds,
        });
      }
    },
    [
      category,
      selectedValueIds,
      existingFilter,
      selectedOperator,
      addFilter,
      updateFilter,
      removeFilter,
    ],
  );

  const changeOperator = useCallback(
    (operator: FilterOperator) => {
      if (existingFilter) {
        updateFilter(existingFilter.id, { operator });
      }
    },
    [existingFilter, updateFilter],
  );

  if (!category) return null;

  return (
    <div className="w-[280px]">
      {/* Header with back button */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <button
          type="button"
          aria-label="Back to filter categories"
          onClick={() => setStep({ type: "category_picker" })}
          className="flex h-6 w-6 items-center justify-center rounded-[6px] text-secondary hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
        <span className="text-[13px] font-medium text-primary">
          {category.label}
        </span>
      </div>

      {/* Operator selector */}
      {category.allowedOperators.length > 1 && (
        <div className="px-3 py-2 border-b border-border">
          <div className="relative inline-flex items-center">
            <select
              value={selectedOperator}
              onChange={(e) => changeOperator(e.target.value as FilterOperator)}
              className="appearance-none bg-card-hover rounded-[6px] pl-2.5 pr-7 py-1 text-[12px] font-medium text-secondary cursor-pointer outline-none hover:text-primary transition-colors"
            >
              {category.allowedOperators.map((op) => (
                <option key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </option>
              ))}
            </select>
            <ChevronDown
              className="absolute right-2 h-3 w-3 text-muted pointer-events-none"
              strokeWidth={1.8}
            />
          </div>
        </div>
      )}

      {/* Search field */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Search className="h-3.5 w-3.5 shrink-0 text-muted" strokeWidth={1.8} />
        <input
          ref={inputRef}
          type="text"
          placeholder={`Search ${category.label.toLowerCase()}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent text-[13px] text-primary placeholder:text-muted outline-none"
        />
      </div>

      {/* Value list */}
      <div className="p-1 max-h-[280px] overflow-y-auto scrollbar-thin">
        {filteredValues.map((value) => {
          const isChecked = selectedValueIds.includes(value.id);
          return (
            <button
              type="button"
              key={value.id}
              onClick={() => toggleValue(value.id)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-secondary hover:bg-card-hover hover:text-primary rounded-[8px] cursor-pointer transition-colors"
            >
              {/* Checkbox */}
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${
                  isChecked
                    ? "bg-accent border-accent"
                    : "border-border bg-transparent"
                }`}
              >
                {isChecked && (
                  <Check className="h-3 w-3 text-white" strokeWidth={2.5} />
                )}
              </span>

              {/* Value icon (optional) */}
              {value.icon && (
                <value.icon
                  className="h-4 w-4 shrink-0"
                  strokeWidth={1.8}
                  style={value.color ? { color: value.color } : undefined}
                />
              )}

              {/* Label */}
              <span className="flex-1 text-left">{value.label}</span>
            </button>
          );
        })}

        {filteredValues.length === 0 && (
          <div className="px-3 py-4 text-[13px] text-muted text-center">
            No matching values
          </div>
        )}
      </div>
    </div>
  );
}
